import { useRef, useLayoutEffect, useState } from 'react';
import { ChartDimensions, ChartBounds } from './types';

interface KeisukeChartProps {
  width?: number;
  height: number;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  children: (dimensions: ChartDimensions) => React.ReactNode;
  backgroundColor?: string;
  plotAreaBackgroundColor?: string;
}

const DEFAULT_MARGIN = {
  top: 40,
  right: 40,
  bottom: 60,
  left: 120, // Y軸ラベルと目盛りラベルのために左マージンを増やす
};

/** width 未指定時の初回レイアウト前フォールバック（ResizeObserver で直ちに置換） */
const DEFAULT_WIDTH = 800;

function isExplicitWidth(width: unknown): width is number {
  return typeof width === 'number' && Number.isFinite(width) && width > 0;
}

/**
 * KeisukeChart - グラフの基盤コンポーネント
 *
 * width を渡さない、または undefined のときは親コンテナ幅いっぱいに追従（ダッシュボード右ペイン等）。
 * 注意: 引数のデフォルト `width = 800` は使わない。undefined が 800 に潰れると右に空白が残る。
 *
 * 親いっぱいモードでは flex 内で SVG に width:100%/height:auto を使うと高さ 0 に潰れる事例があるため、
 * ResizeObserver でコンテナの幅・高さを取り、SVG には viewBox + 明示ピクセル width/height で描画する。
 */
export default function KeisukeChart({
  width,
  height,
  margin = {},
  children,
  backgroundColor = 'transparent',
  plotAreaBackgroundColor = 'transparent',
}: KeisukeChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const explicit = isExplicitWidth(width) ? width : undefined;
  const [autoWidth, setAutoWidth] = useState(DEFAULT_WIDTH);
  /** 親いっぱいモード用: コンテナの実ピクセル幅・高さ（flex で割り当てられた領域） */
  const [observedBox, setObservedBox] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (explicit !== undefined) {
      return;
    }
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      const aw = w > 0 ? Math.max(w, 200) : DEFAULT_WIDTH;
      if (w > 0) {
        setAutoWidth(aw);
      }
      const logicalChartW = explicit ?? aw;
      const aspectFallbackH = Math.max(1, Math.round((height / logicalChartW) * (w > 0 ? w : logicalChartW)));
      if (w > 0 && h > 0) {
        setObservedBox({ width: w, height: h });
      } else if (w > 0 && h === 0) {
        /* flex 確定前などで高さだけ 0 のとき、論理縦横比で仮の高さを入れて SVG を描画する */
        setObservedBox({ width: w, height: aspectFallbackH });
      } else {
        setObservedBox(null);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(measure);
    });
    return () => {
      ro.disconnect();
      cancelAnimationFrame(outerRaf);
      if (innerRaf !== 0) {
        cancelAnimationFrame(innerRaf);
      }
    };
  }, [explicit, height]);

  const finalMargin = {
    top: margin.top ?? DEFAULT_MARGIN.top,
    right: margin.right ?? DEFAULT_MARGIN.right,
    bottom: margin.bottom ?? DEFAULT_MARGIN.bottom,
    left: margin.left ?? DEFAULT_MARGIN.left,
  };

  const chartWidth = explicit ?? autoWidth;

  const dimensions: ChartDimensions = {
    width: chartWidth,
    height,
    margin: finalMargin,
  };

  const plotAreaBounds: ChartBounds = {
    x: finalMargin.left,
    y: finalMargin.top,
    width: chartWidth - finalMargin.left - finalMargin.right,
    height: height - finalMargin.top - finalMargin.bottom,
  };

  const fillContainer = explicit === undefined;

  /** 論理 viewBox と同じ縦横比での表示ピクセル（親 flex が高さ 0 を返す間は幅ベースで推定） */
  const displayWidth = fillContainer ? Math.max(1, observedBox?.width ?? autoWidth) : chartWidth;
  const displayHeight = fillContainer
    ? observedBox && observedBox.height > 0
      ? observedBox.height
      : Math.max(1, Math.round((height / chartWidth) * displayWidth))
    : height;

  /** flex 親が縦 0 を割り当てたときでも、SVG がクリップされずレイアウト最小高さを確保する */
  const layoutMinHeight = fillContainer ? Math.max(displayHeight, 160) : undefined;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        minWidth: 0,
        backgroundColor,
        ...(fillContainer
          ? {
              flex: '1 1 0',
              minHeight: layoutMinHeight ?? 0,
              display: 'block',
              overflow: 'visible',
            }
          : {}),
      }}
    >
      {!fillContainer ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            minWidth: 0,
          }}
        >
          <svg
            viewBox={`0 0 ${chartWidth} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            width={chartWidth}
            height={height}
            style={{ display: 'block', overflow: 'visible', minWidth: 0 }}
            data-keisuke-chart-graph
          >
            <rect
              x={plotAreaBounds.x}
              y={plotAreaBounds.y}
              width={plotAreaBounds.width}
              height={plotAreaBounds.height}
              fill={plotAreaBackgroundColor}
            />
            {children(dimensions)}
          </svg>
        </div>
      ) : (
        <svg
          viewBox={`0 0 ${chartWidth} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          width={displayWidth}
          height={displayHeight}
          style={{
            display: 'block',
            overflow: 'visible',
            minWidth: 0,
            maxWidth: '100%',
            verticalAlign: 'top',
          }}
          data-keisuke-chart-graph
        >
          <rect
            x={plotAreaBounds.x}
            y={plotAreaBounds.y}
            width={plotAreaBounds.width}
            height={plotAreaBounds.height}
            fill={plotAreaBackgroundColor}
          />
          {children(dimensions)}
        </svg>
      )}
    </div>
  );
}
